import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './RecyclingGame.css';
import recycleGlassImg from '../assets/images/recycle-game/recycle-glass.jpg';
import itemPickleJarImg from '../assets/images/recycle-game/items/item-pickle-jar.jpg';
import recycleMetalImg from '../assets/images/recycle-game/recycle-metal.jpg';
import itemPlasticBagImg from '../assets/images/recycle-game/items/item-plastic-bag.jpg';
import itemYogurtContainerImg from '../assets/images/recycle-game/items/item-yogurt-container.jpg';
import recyclePlasticImg from '../assets/images/recycle-game/recycle-plastic.jpg';
import recyclePaperImg from '../assets/images/recycle-game/recycle-paper.jpg';
import recycleOrganicImg from '../assets/images/recycle-game/recycle-organic.jpg';
import itemWineBottleImg from '../assets/images/recycle-game/items/item-wine-bottle.jpg';
import itemJamJarImg from '../assets/images/recycle-game/items/item-jam-jar.jpg';
import itemGlassBottleImg from '../assets/images/recycle-game/items/item-glass-bottle.jpg';
import itemBrokenGlassImg from '../assets/images/recycle-game/items/item-broken-glass.jpg';
import itemShampooBottleImg from '../assets/images/recycle-game/items/item-shampoo-bottle.jpg';
import itemSodaCanImg from '../assets/images/recycle-game/items/item-soda-can.jpg';
import itemBottleCapImg from '../assets/images/recycle-game/items/item-bottle-cap.jpg';
import itemFoodCanImg from '../assets/images/recycle-game/items/item-food-can.jpg';
import itemAluminiumFoilImg from '../assets/images/recycle-game/items/item-aluminium-foil.jpg';
import itemNewspaperImg from '../assets/images/recycle-game/items/item-newspaper.jpg';
import itemCardboardBoxImg from '../assets/images/recycle-game/items/item-cardboard-box.jpg';
import itemMagazineImg from '../assets/images/recycle-game/items/item-magazine.jpg';
import itemPaperBagImg from '../assets/images/recycle-game/items/item-paper-bag.jpg';
import itemCandyWrapperImg from '../assets/images/recycle-game/items/item-candy-wrapper.jpg';
import itemDirtyNappyImg from '../assets/images/recycle-game/items/item-dirty-nappy.jpg';
import itemStyrofoamImg from '../assets/images/recycle-game/items/item-styrofoam.jpg';
import itemWaterBottleImg from '../assets/images/recycle-game/items/item-water-bottle.jpg';
import itemBananaPeelImg from '../assets/images/recycle-game/items/item-banana-peel.jpg';
import itemAppleCoreImg from '../assets/images/recycle-game/items/item-apple-core.jpg';
import itemCoffeeGroundsImg from '../assets/images/recycle-game/items/item-coffee-grounds.jpg';
import itemVegetableScrapsImg from '../assets/images/recycle-game/items/item-vegetable-scraps.jpg';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BinCategory = 'glass' | 'plastic' | 'metal' | 'cardPaper' | 'organic' | 'nonRecyclable';
type Difficulty = 'easy' | 'medium' | 'hard';

interface RecyclableItem {
  id: string;
  name: string;
  nameKey: string;
  category: BinCategory;
  emoji: string;
  image?: string;
  position: { x: number; y: number };
  isDragging: boolean;
  hasBeenIncorrect: boolean;
  justSpawned?: boolean;
}

interface QueuedItem {
  id: string;
  name: string;
  nameKey: string;
  category: BinCategory;
  emoji: string;
  image?: string;
}

interface RecyclingBin {
  id: string;
  name: string;
  category: BinCategory;
  color: string;
  emoji: string;
  image?: string;
}

interface GameState {
  score: number;
  incorrectAttempts: number;
  itemsProcessed: number;
  missedItems: number;
  gameActive: boolean;
  gamePaused: boolean;
  selectedItem: string | null;
  showModal: boolean;
  difficulty: Difficulty | null;
  gameStartTime: number | null;
  gameTimeRemaining: number;
  gameFinished: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GAME_DURATION_SECONDS = 120;
const MAX_ACTIVE_ITEMS = 5;
const FALL_SPEED_PX = 3;
const ITEM_WIDTH = 90;
const ITEM_HEIGHT = 90;
const QUEUE_SIZE = 3;

const SPAWN_DELAYS: Record<Difficulty, [number, number]> = {
  easy:   [5000, 5000], // 6–9 s
  medium: [3500, 2000], // 3.5–5.5 s
  hard:   [1300, 1300], // 1.5–3 s
};

const RECYCLABLE_ITEMS = [
  { nameKey: 'wineBottle',      name: 'Wine Bottle',      category: 'glass'         as BinCategory, emoji: '🍷', image: itemWineBottleImg },
  { nameKey: 'jamJar',          name: 'Jam Jar',          category: 'glass'         as BinCategory, emoji: '🫙', image: itemJamJarImg },
  { nameKey: 'glassBottle',     name: 'Glass Bottle',     category: 'glass'         as BinCategory, emoji: '🍾', image: itemGlassBottleImg },
  { nameKey: 'pickleJar',       name: 'Pickle Jar',       category: 'glass'         as BinCategory, emoji: '🥒', image: itemPickleJarImg },
  { nameKey: 'waterBottle',     name: 'Water Bottle',     category: 'plastic'       as BinCategory, emoji: '🧴', image: itemWaterBottleImg },
  { nameKey: 'plasticBag',      name: 'Plastic Bag',      category: 'plastic'       as BinCategory, emoji: '🛍️', image: itemPlasticBagImg },
  { nameKey: 'yogurtContainer', name: 'Yogurt Container', category: 'plastic'       as BinCategory, emoji: '🥛', image: itemYogurtContainerImg },
  { nameKey: 'shampooBottle',   name: 'Shampoo Bottle',   category: 'plastic'       as BinCategory, emoji: '🧴', image: itemShampooBottleImg },
  { nameKey: 'sodaCan',         name: 'Soda Can',         category: 'metal'         as BinCategory, emoji: '🥤', image: itemSodaCanImg },
  { nameKey: 'foodCan',         name: 'Food Can',         category: 'metal'         as BinCategory, emoji: '🥫', image: itemFoodCanImg },
  { nameKey: 'aluminumFoil',    name: 'Aluminium Foil',  category: 'metal'         as BinCategory, emoji: '📄', image: itemAluminiumFoilImg },
  { nameKey: 'bottleCap',       name: 'Bottle Cap',       category: 'metal'         as BinCategory, emoji: '🔘', image: itemBottleCapImg },
  { nameKey: 'newspaper',       name: 'Newspaper',        category: 'cardPaper'     as BinCategory, emoji: '📰', image: itemNewspaperImg },
  { nameKey: 'cardboardBox',    name: 'Cardboard Box',    category: 'cardPaper'     as BinCategory, emoji: '📦', image: itemCardboardBoxImg },
  { nameKey: 'magazine',        name: 'Magazine',         category: 'cardPaper'     as BinCategory, emoji: '📖', image: itemMagazineImg },
  { nameKey: 'paperBag',        name: 'Paper Bag',        category: 'cardPaper'     as BinCategory, emoji: '🛍️', image: itemPaperBagImg },
  { nameKey: 'appleCore',       name: 'Apple Core',       category: 'organic'       as BinCategory, emoji: '🍎', image: itemAppleCoreImg },
  { nameKey: 'bananaPeel',      name: 'Banana Peel',      category: 'organic'       as BinCategory, emoji: '🍌', image: itemBananaPeelImg },
  { nameKey: 'coffeeGrounds',   name: 'Coffee Grounds',   category: 'organic'       as BinCategory, emoji: '☕', image: itemCoffeeGroundsImg },
  { nameKey: 'vegetableScraps', name: 'Vegetable Scraps', category: 'organic'       as BinCategory, emoji: '🥬', image: itemVegetableScrapsImg },
  { nameKey: 'dirtyNappy',     name: 'Dirty Nappy',     category: 'nonRecyclable' as BinCategory, emoji: '👶', image: itemDirtyNappyImg },
  { nameKey: 'candyWrapper',    name: 'Candy Wrapper',    category: 'nonRecyclable' as BinCategory, emoji: '🍬', image: itemCandyWrapperImg },
  { nameKey: 'brokenGlass',     name: 'Broken Glass',     category: 'nonRecyclable' as BinCategory, emoji: '⚠️', image: itemBrokenGlassImg },
  { nameKey: 'styrofoam',       name: 'Styrofoam',        category: 'nonRecyclable' as BinCategory, emoji: '📦', image: itemStyrofoamImg },
];

const RECYCLING_BINS: RecyclingBin[] = [
  { id: 'glass',         name: 'Glass',          category: 'glass',         color: '#4CAF50', emoji: '♻️', image: recycleGlassImg },
  { id: 'plastic',       name: 'Plastic',        category: 'plastic',       color: '#2196F3', emoji: '♻️', image: recyclePlasticImg },
  { id: 'metal',         name: 'Metal',          category: 'metal',         color: '#FF9800', emoji: '♻️', image: recycleMetalImg },
  { id: 'cardPaper',     name: 'Card & Paper',   category: 'cardPaper',     color: '#8BC34A', emoji: '♻️', image: recyclePaperImg },
  { id: 'organic',       name: 'Organic',        category: 'organic',       color: '#795548', emoji: '♻️', image: recycleOrganicImg },
  { id: 'nonRecyclable', name: 'Non-recyclable', category: 'nonRecyclable', color: '#F44336', emoji: '🗑️' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const IS_TOUCH_DEVICE =
  typeof window !== 'undefined' &&
  (navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches);

const useIsLandscape = (): boolean => {
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape)');
    const handler = (e: MediaQueryListEvent) => setIsLandscape(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isLandscape;
};

const randomItem = () => RECYCLABLE_ITEMS[Math.floor(Math.random() * RECYCLABLE_ITEMS.length)];

const makeQueuedItem = (): QueuedItem => {
  const item = randomItem();
  return { id: `q-${Date.now()}-${Math.random().toString(36).slice(2)}`, ...item };
};

const makeActiveItem = (queued: QueuedItem, gameAreaWidth: number): RecyclableItem => ({
  id: `item-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: queued.name,
  nameKey: queued.nameKey,
  category: queued.category,
  emoji: queued.emoji,
  image: queued.image,
  position: { x: Math.max(20, Math.random() * (gameAreaWidth - ITEM_WIDTH - 20)), y: -ITEM_HEIGHT },
  isDragging: false,
  hasBeenIncorrect: false,
  justSpawned: true,
});

const formatTime = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RecyclingGame: React.FC = () => {
  const { t } = useTranslation();
  const isLandscape = useIsLandscape();

  const [gameState, setGameState] = useState<GameState>({
    score: 0,
    incorrectAttempts: 0,
    itemsProcessed: 0,
    missedItems: 0,
    gameActive: false,
    gamePaused: false,
    selectedItem: null,
    showModal: false,
    difficulty: null,
    gameStartTime: null,
    gameTimeRemaining: GAME_DURATION_SECONDS,
    gameFinished: false,
  });

  const [activeItems, setActiveItems] = useState<RecyclableItem[]>([]);
  const [itemQueue, setItemQueue] = useState<QueuedItem[]>([]);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [highScores, setHighScores] = useState<number[]>([]);
  const [binFeedback, setBinFeedback] = useState<Record<string, 'correct' | 'incorrect'>>({});

  const gameAreaRef   = useRef<HTMLDivElement>(null);
  const spawnTimerRef = useRef<NodeJS.Timeout | null>(null);
  const binRefs       = useRef<Record<string, HTMLDivElement | null>>({});
  const itemQueueRef  = useRef<QueuedItem[]>([]);
  const activeItemsRef = useRef<RecyclableItem[]>([]);
  const gameStateRef   = useRef(gameState);
  const draggingItemsRef       = useRef<Set<string>>(new Set());
  const binFeedbackTimersRef   = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => { gameStateRef.current  = gameState;  }, [gameState]);
  useEffect(() => { activeItemsRef.current = activeItems; }, [activeItems]);
  useEffect(() => { itemQueueRef.current  = itemQueue;  }, [itemQueue]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('recyclingHighScores');
      if (raw) {
        const parsed = JSON.parse(raw) as number[];
        if (Array.isArray(parsed)) setHighScores(parsed.slice(-5));
      }
    } catch { /* ignore */ }
  }, []);

  // ---------------------------------------------------------------------------
  // Feedback
  // ---------------------------------------------------------------------------

  const showFeedbackMessage = useCallback((message: string) => {
    setFeedbackMessage(message);
    setTimeout(() => setFeedbackMessage(null), 2000);
  }, []);

  const flashBin = useCallback((category: string, result: 'correct' | 'incorrect') => {
    if (binFeedbackTimersRef.current[category]) clearTimeout(binFeedbackTimersRef.current[category]);
    setBinFeedback(prev => ({ ...prev, [category]: result }));
    binFeedbackTimersRef.current[category] = setTimeout(() => {
      setBinFeedback(prev => { const next = { ...prev }; delete next[category]; return next; });
    }, 800);
  }, []);

  // ---------------------------------------------------------------------------
  // Spawning
  // ---------------------------------------------------------------------------

  const spawnItem = useCallback(() => {
    const gs = gameStateRef.current;
    if (!gs.gameActive || gs.gamePaused) return;
    if (activeItemsRef.current.length >= MAX_ACTIVE_ITEMS) return;

    const queue = itemQueueRef.current;
    if (queue.length === 0) return;

    const [next, ...rest] = queue;
    const newQueue = [...rest, ...Array.from({ length: QUEUE_SIZE - rest.length }, makeQueuedItem)];
    itemQueueRef.current = newQueue;
    setItemQueue(newQueue);

    const width = gameAreaRef.current?.clientWidth ?? 400;
    const newItem = makeActiveItem(next, width);
    setActiveItems(prev => [...prev, newItem]);

    // Clear justSpawned after two frames so the item fades in rather than flashing at (0,0)
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        setActiveItems(prev => prev.map(i => i.id === newItem.id ? { ...i, justSpawned: false } : i))
      )
    );
  }, []);

  const scheduleNextSpawn = useCallback((difficulty: Difficulty) => {
    if (spawnTimerRef.current) clearTimeout(spawnTimerRef.current);
    const [base, rand] = SPAWN_DELAYS[difficulty];
    spawnTimerRef.current = setTimeout(() => {
      spawnItem();
      scheduleNextSpawn(difficulty);
    }, base + Math.random() * rand);
  }, [spawnItem]);

  // ---------------------------------------------------------------------------
  // Game lifecycle
  // ---------------------------------------------------------------------------

  const clearAllTimers = useCallback(() => {
    if (spawnTimerRef.current) { clearTimeout(spawnTimerRef.current); spawnTimerRef.current = null; }
  }, []);

  const resetGameItems = useCallback(() => {
    setActiveItems([]);
    setItemQueue([]);
    itemQueueRef.current = [];
    draggingItemsRef.current.clear();
  }, []);

  const endGame = useCallback(() => {
    clearAllTimers();
    const finalScore = gameStateRef.current.score;
    setGameState(prev => ({ ...prev, gameActive: false, gamePaused: false, gameFinished: true }));
    resetGameItems();
    setHighScores(prev => {
      const updated = [...prev, finalScore].slice(-5);
      try { localStorage.setItem('recyclingHighScores', JSON.stringify(updated)); } catch { /* ignore */ }
      return updated;
    });
  }, [clearAllTimers, resetGameItems]);

  const startGameWithDifficulty = useCallback((difficulty: Difficulty) => {
    clearAllTimers();
    resetGameItems();
    setFeedbackMessage(null);

    const initialQueue = Array.from({ length: QUEUE_SIZE }, makeQueuedItem);
    itemQueueRef.current = initialQueue;
    setItemQueue(initialQueue);

    setGameState({
      score: 0, incorrectAttempts: 0, itemsProcessed: 0, missedItems: 0,
      gameActive: true, gamePaused: false, selectedItem: null, showModal: true,
      difficulty, gameStartTime: Date.now(),
      gameTimeRemaining: GAME_DURATION_SECONDS, gameFinished: false,
    });

    setTimeout(() => { spawnItem(); scheduleNextSpawn(difficulty); }, 300);
  }, [clearAllTimers, resetGameItems, spawnItem, scheduleNextSpawn]);

  const closeGameModal = useCallback(() => {
    clearAllTimers();
    resetGameItems();
    setGameState(prev => ({ ...prev, showModal: false, gameActive: false, gamePaused: false, difficulty: null }));
  }, [clearAllTimers, resetGameItems]);

  const restartGame = useCallback(() => {
    clearAllTimers();
    resetGameItems();
    setGameState(prev => ({
      ...prev, gameActive: false, gamePaused: false, gameFinished: false,
      difficulty: null, gameStartTime: null, gameTimeRemaining: GAME_DURATION_SECONDS,
    }));
  }, [clearAllTimers, resetGameItems]);

  const pauseGame = () => {
    setGameState(prev => {
      const nowPaused = !prev.gamePaused;
      if (!nowPaused && prev.difficulty) scheduleNextSpawn(prev.difficulty);
      else clearAllTimers();
      return { ...prev, gamePaused: nowPaused };
    });
  };

  // ---------------------------------------------------------------------------
  // Item ↔ Bin interaction
  // ---------------------------------------------------------------------------

  const handleItemToBin = useCallback((itemId: string, binCategory: string) => {
    const item = activeItemsRef.current.find(i => i.id === itemId);
    if (!item) return;

    draggingItemsRef.current.delete(itemId);

    if (item.category === binCategory) {
      setGameState(prev => ({
        ...prev,
        score: prev.score + (item.hasBeenIncorrect ? 5 : 10),
        itemsProcessed: prev.itemsProcessed + 1,
        selectedItem: null,
      }));
      setActiveItems(prev => prev.filter(i => i.id !== itemId));
      flashBin(binCategory, 'correct');
      showFeedbackMessage(t('recyclingGame.feedback.correct'));
    } else {
      setGameState(prev => ({
        ...prev,
        incorrectAttempts: prev.incorrectAttempts + 1,
        score: Math.max(0, prev.score - 1),
        selectedItem: null,
      }));
      const startX = Math.max(20, Math.random() * ((gameAreaRef.current?.clientWidth ?? 400) - ITEM_WIDTH - 20));
      setActiveItems(prev =>
        prev.map(i => i.id === itemId
          ? { ...i, hasBeenIncorrect: true, isDragging: false, position: { x: startX, y: -ITEM_HEIGHT } }
          : i
        )
      );
      flashBin(binCategory, 'incorrect');
      showFeedbackMessage(t('recyclingGame.feedback.tryAgain', {
        name:     t(`recyclingGame.items.${item.nameKey}`),
        category: t(`recyclingGame.bins.${item.category}`),
      }));
    }
  }, [showFeedbackMessage, flashBin]);

  const handleItemClick = (itemId: string) =>
    setGameState(prev => ({ ...prev, selectedItem: prev.selectedItem === itemId ? null : itemId }));

  const handleBinClick = (binCategory: string) => {
    const { selectedItem } = gameStateRef.current;
    const target = selectedItem ?? (activeItemsRef.current.length === 1 ? activeItemsRef.current[0].id : null);
    if (target) handleItemToBin(target, binCategory);
  };

  // ---------------------------------------------------------------------------
  // Drag — mouse (global listeners) + touch (element handlers)
  // ---------------------------------------------------------------------------

  // Active drag state: item being dragged and cursor-to-item offset
  const activeDragRef = useRef<{ itemId: string; offsetX: number; offsetY: number } | null>(null);

  const startDrag = (itemId: string, clientX: number, clientY: number) => {
    const gameArea = gameAreaRef.current;
    const item = activeItemsRef.current.find(i => i.id === itemId);
    if (!gameArea || !item) return;
    const r = gameArea.getBoundingClientRect();
    activeDragRef.current = {
      itemId,
      offsetX: (clientX - r.left) - item.position.x,
      offsetY: (clientY - r.top)  - item.position.y,
    };
    draggingItemsRef.current.add(itemId);
  };

  const moveDrag = useCallback((clientX: number, clientY: number) => {
    const drag = activeDragRef.current;
    const gameArea = gameAreaRef.current;
    if (!drag || !gameArea) return;
    const r = gameArea.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - r.left - drag.offsetX, gameArea.clientWidth  - ITEM_WIDTH));
    const y = Math.max(0, Math.min(clientY - r.top  - drag.offsetY, gameArea.clientHeight - ITEM_HEIGHT));
    setActiveItems(prev =>
      prev.map(i => i.id === drag.itemId ? { ...i, isDragging: true, position: { x, y } } : i)
    );
  }, []);

  const commitDrop = useCallback((clientX: number, clientY: number) => {
    const drag = activeDragRef.current;
    if (!drag) return;
    activeDragRef.current = null;
    draggingItemsRef.current.delete(drag.itemId);

    // Check if dropped on a bin
    const binEl = document.elementFromPoint(clientX, clientY)?.closest('.recycling-bin');
    const binCategory = binEl?.getAttribute('data-category');
    if (binCategory) {
      handleItemToBin(drag.itemId, binCategory);
      return;
    }

    // Released mid-air — keep current position, resume falling
    setActiveItems(prev =>
      prev.map(i => i.id === drag.itemId ? { ...i, isDragging: false } : i)
    );
  }, [handleItemToBin]);

  // Mouse drag uses global document listeners so it works even outside the item element
  useEffect(() => {
    const onMove = (e: MouseEvent) => moveDrag(e.clientX, e.clientY);
    const onUp   = (e: MouseEvent) => commitDrop(e.clientX, e.clientY);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [moveDrag, commitDrop]);

  // ---------------------------------------------------------------------------
  // Game timer
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!gameState.gameActive || gameState.gamePaused || !gameState.gameStartTime) return;
    const interval = setInterval(() => {
      setGameState(prev => {
        const remaining = Math.max(0, GAME_DURATION_SECONDS - Math.floor((Date.now() - (prev.gameStartTime ?? 0)) / 1000));
        if (remaining === 0 && prev.gameActive) setTimeout(() => endGame(), 100);
        return { ...prev, gameTimeRemaining: remaining };
      });
    }, 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.gameActive, gameState.gamePaused, gameState.gameStartTime]);

  // ---------------------------------------------------------------------------
  // Falling animation & bin collision
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!gameState.gameActive || gameState.gamePaused) return;

    const interval = setInterval(() => {
      const gameArea = gameAreaRef.current;
      if (!gameArea) return;
      const gameAreaHeight = gameArea.clientHeight;
      const gameAreaRect   = gameArea.getBoundingClientRect();

      // Snapshot bin positions (must be outside state updater — DOM reads aren't allowed inside)
      const binRects = RECYCLING_BINS.map(bin => {
        const el = binRefs.current[bin.category];
        if (!el) return null;
        const br = el.getBoundingClientRect();
        return {
          category: bin.category,
          left:  br.left  - gameAreaRect.left,
          right: br.right - gameAreaRect.left,
          top:   br.top   - gameAreaRect.top,
        };
      }).filter(Boolean) as { category: string; left: number; right: number; top: number }[];

      // Compute outcomes against the current snapshot of activeItems (via ref)
      // This must happen outside the state updater so it runs exactly once
      const currentItems = activeItemsRef.current;
      const placements: { itemId: string; binCategory: string }[] = [];
      const removals   = new Set<string>();
      let   missedCount = 0;

      const nextPositions: Record<string, { x: number; y: number }> = {};

      currentItems.forEach(item => {
        if (item.isDragging || draggingItemsRef.current.has(item.id)) return;

        const newY       = item.position.y + FALL_SPEED_PX;
        const centerX    = item.position.x + ITEM_WIDTH / 2;
        const bottomEdge = newY + ITEM_HEIGHT;
        const overBin    = (b: typeof binRects[0]) => centerX >= b.left && centerX <= b.right;

        const hitBin = binRects.find(b => overBin(b) && bottomEdge >= b.top);
        if (hitBin) {
          placements.push({ itemId: item.id, binCategory: hitBin.category });
          removals.add(item.id);
          return;
        }

        if (newY >= gameAreaHeight) {
          removals.add(item.id);
          missedCount++;
          return;
        }

        nextPositions[item.id] = { x: item.position.x, y: newY };
      });

      // Apply position updates and removals in a single state update
      if (removals.size > 0 || Object.keys(nextPositions).length > 0) {
        setActiveItems(prev =>
          prev
            .filter(i => !removals.has(i.id))
            .map(i => nextPositions[i.id] ? { ...i, position: nextPositions[i.id] } : i)
        );
      }

      if (missedCount > 0) {
        setGameState(gs => ({
          ...gs,
          missedItems: gs.missedItems + missedCount,
          score: Math.max(0, gs.score - missedCount),
        }));
      }

      placements.forEach(p => handleItemToBin(p.itemId, p.binCategory));
    }, 50);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.gameActive, gameState.gamePaused]);

  useEffect(() => () => clearAllTimers(), [clearAllTimers]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const isUrgent  = gameState.gameTimeRemaining <= 10 && gameState.gameActive;
  const isWarning = gameState.gameTimeRemaining <= 30 && gameState.gameActive;

  return (
    <div className="recycling-game">
      <div className="game-header">
        <div className="header-left">
          <h2>🐞 {t('recyclingGame.title')}</h2>
          {!gameState.showModal && (
            <div className="landing-instructions">
              {t('recyclingGame.tagline')}{' '}
              {IS_TOUCH_DEVICE
                ? t('recyclingGame.instructionsTouch')
                : t('recyclingGame.instructionsMouse')}
            </div>
          )}
        </div>
        <div className="game-controls">
          {!gameState.gameActive ? (
            <button onClick={() => setGameState(prev => ({ ...prev, showModal: true }))} className="start-btn">{t('recyclingGame.startGame')}</button>
          ) : (
            <>
              <button onClick={pauseGame} className="pause-btn">{gameState.gamePaused ? t('recyclingGame.resume') : t('recyclingGame.pause')}</button>
              <button onClick={endGame}   className="end-btn">{t('recyclingGame.endGame')}</button>
            </>
          )}
        </div>
      </div>

      {!gameState.showModal && (
        <div className="landing-content">
          <div className="highscores">
            <div className="highscores-title">{t('recyclingGame.highScores')}</div>
            <ol className="highscores-list">
              {[...highScores].sort((a, b) => b - a).map((score, idx) => (
                <li key={`${score}-${idx}`} className="highscore-item">{score}</li>
              ))}
              {highScores.length === 0 && <li className="highscore-item">{t('recyclingGame.noScores')}</li>}
            </ol>
          </div>
        </div>
      )}

      {feedbackMessage && <div className="feedback-message">{feedbackMessage}</div>}

      {gameState.showModal && (
        <div className="game-modal-overlay">
          <div className="game-modal">
            <div className="modal-header">
              <h2>🐞 {t('recyclingGame.title')}</h2>
              {IS_TOUCH_DEVICE && gameState.gameActive && (
                <span className="modal-touch-hint">{t('recyclingGame.touchHint')}</span>
              )}
              <button onClick={closeGameModal} className="close-btn">✕</button>
            </div>

            {gameState.gameFinished ? (
              /* ---- Results screen ---- */
              <div className="game-results">
                <h3>🎉 {t('recyclingGame.results.title')}</h3>
                <div className="results-summary">
                  <div className="final-score">
                    <span className="score-label">{t('recyclingGame.results.finalScore')}</span>
                    <span className="score-value">{gameState.score}</span>
                  </div>
                  <div className="results-stats">
                    {[
                      { icon: '✅', label: t('recyclingGame.results.itemsRecycled'), value: gameState.itemsProcessed },
                      { icon: '❌', label: t('recyclingGame.results.mistakes'),      value: gameState.incorrectAttempts },
                      { icon: '⏳', label: t('recyclingGame.results.missedItems'),   value: gameState.missedItems },
                      { icon: '🏆', label: t('recyclingGame.results.difficulty'),    value: gameState.difficulty ? t(`recyclingGame.difficulty.${gameState.difficulty}.label`) : '' },
                    ].map(({ icon, label, value }) => (
                      <div key={label} className="result-stat">
                        <span className="stat-icon">{icon}</span>
                        <span className="stat-label">{label}</span>
                        <span className="stat-value">{value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="results-actions">
                    <button onClick={restartGame} className="play-again-btn">🎮 {t('recyclingGame.playAgain')}</button>
                  </div>
                </div>
              </div>

            ) : !gameState.gameActive ? (
              /* ---- Difficulty selection ---- */
              <div className="difficulty-selection">
                <h3>{t('recyclingGame.chooseLevel')}</h3>
                <p>{t('recyclingGame.chooseDesc')}</p>
                <div className="difficulty-buttons">
                  {(['easy', 'medium', 'hard'] as const).map(key => (
                    <button key={key} onClick={() => startGameWithDifficulty(key)} className={`difficulty-btn ${key}`}>
                      <span className="difficulty-icon">{t(`recyclingGame.difficulty.${key}.icon`)}</span>
                      <span className="difficulty-name">{t(`recyclingGame.difficulty.${key}.label`)}</span>
                      <span className="difficulty-desc">{t(`recyclingGame.difficulty.${key}.desc`)}</span>
                    </button>
                  ))}
                </div>
              </div>

            ) : (
              /* ---- Active game ---- */
              <div className={`game-container-modal${isLandscape ? ' landscape' : ''}`}>

                <div className="game-sidebar">
                  <div className={`stat timer-display${isWarning ? ' timer-warning' : ''}${isUrgent ? ' timer-urgent' : ''}`}>
                    <span className="timer-label">{t('recyclingGame.timer')}</span>
                    <span className={`timer-value${isUrgent ? ' timer-urgent-pulse' : ''}`}>
                      {formatTime(gameState.gameTimeRemaining)}
                    </span>
                  </div>

                  <div className="stat-grid">
                    {[
                      { label: t('recyclingGame.stats.score'),    value: gameState.score },
                      { label: t('recyclingGame.stats.items'),    value: gameState.itemsProcessed },
                      { label: t('recyclingGame.stats.mistakes'), value: gameState.incorrectAttempts },
                      { label: t('recyclingGame.stats.missed'),   value: gameState.missedItems },
                    ].map(({ label, value }) => (
                      <div key={label} className="stat-cell">
                        <span className="stat-cell-label">{label}</span>
                        <span className="stat-cell-value">{value}</span>
                      </div>
                    ))}
                  </div>

                  <div className="stat-level">{gameState.difficulty?.toUpperCase()}</div>

                  <div className="upcoming-items-modal">
                    <div className="upcoming-label">{t('recyclingGame.comingUp')}</div>
                    <div className="upcoming-items-list">
                      {itemQueue.map((item, index) => (
                        <div key={item.id} className={`upcoming-item ${index === 0 ? 'next' : ''}`}>
                          {item.image ? (
                            <div className="upcoming-item-image-wrap">
                              <img src={item.image} alt="" className="upcoming-item-image" />
                            </div>
                          ) : (
                            <div className="upcoming-item-emoji">{item.emoji}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="modal-game-controls">
                    <button onClick={pauseGame} className="pause-btn">{gameState.gamePaused ? t('recyclingGame.resume') : t('recyclingGame.pause')}</button>
                    <button onClick={endGame}   className="end-btn">{t('recyclingGame.endGame')}</button>
                  </div>
                </div>

                <div className="modal-game-area" ref={gameAreaRef}>
                  {gameState.gamePaused && (
                    <div className="pause-overlay">
                      <div className="pause-message">{t('recyclingGame.gamePaused')}</div>
                    </div>
                  )}

                  {activeItems.map(item => (
                    <div
                      key={item.id}
                      className={`recyclable-item${item.image ? ' has-image' : ''}${gameState.selectedItem === item.id ? ' selected' : ''}${item.hasBeenIncorrect ? ' incorrect' : ''}${IS_TOUCH_DEVICE ? ' touch-mode' : ''}`}
                      style={{
                        transform: `translate(${item.position.x}px, ${item.position.y}px)`,
                        opacity:    item.justSpawned ? 0 : (item.isDragging ? 0.7 : 1),
                        touchAction: 'none',
                        userSelect:  'none',
                        ...(item.image ? { backgroundImage: `url(${item.image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
                      }}
                      onMouseDown={e => { if (e.button === 0) { e.preventDefault(); startDrag(item.id, e.clientX, e.clientY); } }}
                      onTouchStart={e => { e.preventDefault(); startDrag(item.id, e.touches[0].clientX, e.touches[0].clientY); }}
                      onTouchMove={e  => { e.preventDefault(); moveDrag(e.touches[0].clientX, e.touches[0].clientY); }}
                      onTouchEnd={e   => { e.preventDefault(); commitDrop(e.changedTouches[0].clientX, e.changedTouches[0].clientY); }}
                      onClick={() => handleItemClick(item.id)}
                    >
                      {item.image ? (
                        <>
                          <div className="item-name-wrap item-name-wrap--image">
                            <span className="item-name item-name--on-image">{t(`recyclingGame.items.${item.nameKey}`)}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="item-emoji">{item.emoji}</div>
                          <div className="item-name">{t(`recyclingGame.items.${item.nameKey}`)}</div>
                        </>
                      )}
                    </div>
                  ))}

                  <div className="modal-recycling-bins in-area">
                    {RECYCLING_BINS.map(bin => {
                      const feedback = binFeedback[bin.category];
                      const binEmoji = feedback === 'correct' ? '✅' : feedback === 'incorrect' ? '❌' : bin.emoji;
                      return (
                        <div
                          key={bin.id}
                          className={`recycling-bin${bin.image ? ' has-image' : ''}${feedback ? ` bin-${feedback}` : ''}`}
                          data-category={bin.category}
                          style={bin.image ? { backgroundImage: `url(${bin.image})` } : { backgroundColor: bin.color }}
                          ref={el => { binRefs.current[bin.category] = el; }}
                          onClick={() => handleBinClick(bin.category)}
                        >
                          {bin.image && <div className="bin-image-overlay" style={{ backgroundColor: bin.color }} />}
                          {feedback  && <div className={`bin-feedback-overlay bin-feedback-${feedback}`} />}
                          <div className="bin-emoji">{binEmoji}</div>
                          <div className="bin-name-wrap">
                            <div className="bin-name">{t(`recyclingGame.bins.${bin.category}`)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecyclingGame;
